import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI, planAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { Button, Input, ThemeToggle } from '../common';

const AuthCard = ({ children, subtitle, size = 'md' }) => {
  const { theme, toggleTheme } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Floating theme toggle absolute in top-right corner of entire page */}
      <div className="absolute top-4 right-4 z-50">
        <ThemeToggle theme={theme} onToggle={toggleTheme}/>
      </div>
      
      {/* Ambient background glow elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-brand-500/5 dark:bg-brand-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-indigo-500/5 dark:bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none" />
      
      <div className={`w-full ${size === 'lg' ? 'max-w-xl' : 'max-w-md'} transition-all duration-300 relative z-10`}>
        <div className="text-center mb-6 flex flex-col items-center">
          {/* Visually Impressive App Icon Badge */}
          <div className="w-20 h-20 rounded-3xl bg-black border border-slate-800 shadow-xl shadow-orange-500/25 ring-2 ring-orange-500/30 flex items-center justify-center p-2.5 overflow-hidden transform hover:scale-105 transition-all duration-300">
            <img 
              src="/logo.png" 
              alt="KarmShala Icon" 
              className="w-full h-full object-contain" 
            />
          </div>
          {/* Brand Name Title in English */}
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mt-3 font-display">KarmShala</h1>
          {/* Subtitle / Context Message */}
          <p className="text-slate-500 dark:text-slate-400 text-sm font-semibold mt-1">{subtitle}</p>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-800/80 p-8 transition-all hover:shadow-2xl duration-350">
          {children}
        </div>
      </div>
    </div>
  );
};

// ─── Login ────────────────────────────────────────────────────────────────────
export const LoginPage = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [tab, setTab]         = useState('email');     // 'email' | 'mobile'
  const [form, setForm]       = useState({ email:'', password:'', mobile:'', otp:'' });
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const sendOTP = async () => {
    if (!form.mobile.trim()) return toast.error('Enter your mobile number');
    setSending(true);
    try {
      await authAPI.requestOTP(form.mobile.trim());
      setOtpSent(true);
      toast.success('OTP sent to your mobile');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally { setSending(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let data;
      if (tab === 'email') {
        ({ data } = await authAPI.loginEmail({ email: form.email, password: form.password }));
      } else {
        ({ data } = await authAPI.verifyOTP(form.mobile.trim(), form.otp.trim()));
      }
      setAuth(data.data, data.data.user);
      navigate(data.data.user.role === 'superadmin' ? '/superadmin' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally { setLoading(false); }
  };

  const tabClass = (t) =>
    `flex-1 py-2 text-sm font-semibold rounded-xl transition-all duration-200 ${
      tab === t
        ? 'bg-brand-600 text-white shadow-md shadow-brand-500/10'
        : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
    }`;

  return (
    <AuthCard subtitle="Sign in to your workspace" size="md">
      {/* Tab switcher */}
      <div className="flex gap-1.5 p-1.5 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800/60 rounded-2xl mb-6">
        <button type="button" className={tabClass('email')}  onClick={() => { setTab('email');  setOtpSent(false); }}>Email</button>
        <button type="button" className={tabClass('mobile')} onClick={() => { setTab('mobile'); setOtpSent(false); }}>Mobile OTP</button>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {tab === 'email' ? (
          <>
            <Input label="Email" name="email" type="email" placeholder="you@company.com" value={form.email} onChange={handle} required/>
            <Input label="Password" name="password" type="password" placeholder="••••••••" value={form.password} onChange={handle} required/>
          </>
        ) : (
          <>
            <div className="space-y-1">
              <Input label="Mobile Number" name="mobile" type="tel" placeholder="+91 9876543210" value={form.mobile} onChange={handle} required/>
              {!otpSent && (
                <Button type="button" variant="secondary" className="w-full mt-2" onClick={sendOTP} loading={sending}>Send OTP</Button>
              )}
            </div>
            {otpSent && (
              <div className="space-y-2">
                <Input label="Enter OTP" name="otp" type="text" inputMode="numeric" maxLength={6} placeholder="6-digit OTP" value={form.otp} onChange={handle} required/>
                <button type="button" onClick={() => { setOtpSent(false); setForm({...form, otp:''}); }} className="text-xs text-brand-600 hover:underline">Resend OTP</button>
              </div>
            )}
          </>
        )}

        <Button type="submit" className="w-full py-2.5 shadow-lg shadow-brand-500/10" loading={loading}
          disabled={tab === 'mobile' && !otpSent}>
          {tab === 'mobile' ? 'Verify & Sign in' : 'Sign in'}
        </Button>
      </form>

      <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
        New here?{' '}
        <Link to="/register" className="text-brand-600 font-medium hover:underline">Create a workspace</Link>
      </p>
      <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/60 text-center text-[11px] text-slate-400 dark:text-slate-500 space-y-1">
        <p>By continuing, you agree to our</p>
        <div className="flex items-center justify-center gap-2 flex-wrap font-semibold text-indigo-500">
          <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
          <span>•</span>
          <Link to="/terms" className="hover:underline">Terms &amp; Conditions</Link>
          <span>•</span>
          <Link to="/refund-policy" className="hover:underline">Refund Policy</Link>
          <span>•</span>
          <Link to="/contact-us" className="hover:underline">Contact Us</Link>
        </div>
      </div>
    </AuthCard>
  );
};

// Helper to dynamically load Razorpay checkout script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// ─── Register ─────────────────────────────────────────────────────────────────
export const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuth();
  
  const queryParams = new URLSearchParams(location.search);
  const initialPlanId = queryParams.get('planId') || '';

  const [form, setForm]       = useState({ tenantName:'', name:'', email:'', mobile:'', password:'', planId: initialPlanId });
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    planAPI.getAll()
      .then(res => {
        const availablePlans = res.data.data.plans || [];
        setPlans(availablePlans);
        if (!form.planId && availablePlans.length > 0) {
          const freeOrFirst = availablePlans.find(p => p.isFree) || availablePlans[0];
          if (freeOrFirst) setForm(f => ({ ...f, planId: freeOrFirst._id }));
        }
      })
      .catch(() => {});
  }, []);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const selectedPlan = plans.find(p => p._id === form.planId);
  const isPaidPlan = selectedPlan && !selectedPlan.isFree && selectedPlan.price > 0;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!isPaidPlan) {
        // ── 1. Free Plan: Direct registration without payment ──
        const { data } = await authAPI.register(form);
        setAuth(data.data, data.data.user);
        toast.success('Workspace created! Welcome to KarmShala 🎉');
        navigate('/dashboard');
      } else {
        // ── 2. Paid Plan: Trigger Razorpay Live Checkout ──
        const sdkLoaded = await loadRazorpayScript();
        if (!sdkLoaded) {
          toast.error('Razorpay SDK failed to load. Check your internet connection.');
          setLoading(false);
          return;
        }

        // Step A: Create Order on Backend
        const orderRes = await authAPI.createRegistrationOrder(form);
        const orderData = orderRes.data.data;

        // Step B: Configure Razorpay Checkout Modal
        const options = {
          key: orderData.keyId,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'KarmShala SaaS',
          description: `${orderData.planName} Subscription — ₹${orderData.price}/month`,
          image: '/logo.png',
          order_id: orderData.orderId,
          prefill: {
            name: form.name,
            email: form.email,
            contact: form.mobile,
          },
          theme: { color: '#ff6b00' },
          handler: async (response) => {
            try {
              toast.loading('Verifying Razorpay payment...', { id: 'regpay' });
              const verifyRes = await authAPI.verifyRegistrationPayment({
                ...form,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              const vData = verifyRes.data.data;
              setAuth(vData, vData.user);
              toast.success('Payment Verified! Workspace Created 🎉', { id: 'regpay' });
              navigate('/dashboard');
            } catch (vErr) {
              toast.error(vErr.response?.data?.message || 'Payment verification failed', { id: 'regpay' });
            } finally {
              setLoading(false);
            }
          },
          modal: {
            ondismiss: () => {
              toast.error('Payment process was cancelled');
              setLoading(false);
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
      setLoading(false);
    }
  };

  return (
    <AuthCard subtitle="Set up your new KarmShala workspace in seconds" size="lg">
      <form onSubmit={submit} className="space-y-6">
        {/* Organisation Name - Full Width at Top */}
        <div className="pb-4 border-b border-slate-100 dark:border-slate-800/60">
          <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3">Organisation</p>
          <Input label="Organisation Name *" name="tenantName" placeholder="Acme Enterprise" value={form.tenantName} onChange={handle} required/>
        </div>

        {/* Plan Dropdown & Credentials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
              Select SaaS Subscription Plan *
            </label>
            <select
              name="planId"
              value={form.planId}
              onChange={handle}
              required
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-semibold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500/20 outline-none"
            >
              <option value="">-- Select Subscription Plan --</option>
              {plans.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name} — {p.isFree ? 'Free (₹0/month)' : `₹${p.price}/month`} ({p.features?.maxUsers ?? 5} Max Users Quota)
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 pt-2">
            <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">Admin Account Details</p>
          </div>
          <Input label="Admin Full Name *" name="name" placeholder="John Doe" value={form.name} onChange={handle} required/>
          <Input label="Email Address *" name="email" type="email" placeholder="admin@acme.com" value={form.email} onChange={handle} required/>
          <Input label="Mobile Number *" name="mobile" type="tel" placeholder="+91 9876543210" value={form.mobile} onChange={handle} required hint="Used for WhatsApp OTP login"/>
          <Input label="Account Password *" name="password" type="password" placeholder="Min 6 characters" value={form.password} onChange={handle} required minLength={6}/>
        </div>

        {/* Full-width Centered Button at the bottom */}
        <div className="pt-4 border-t border-slate-100 dark:border-slate-800/60">
          <Button type="submit" className="w-full py-3 text-sm font-bold shadow-lg shadow-brand-500/10" loading={loading}>
            {isPaidPlan ? `Proceed to Pay ₹${selectedPlan.price} & Create Workspace` : 'Create Workspace Free'}
          </Button>
        </div>
      </form>
      <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
        Already have a KarmShala account?{' '}
        <Link to="/login" className="text-brand-600 font-medium hover:underline">Sign in</Link>
      </p>
      <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/60 text-center text-[11px] text-slate-400 dark:text-slate-500 space-y-1">
        <p>By creating a workspace, you agree to our</p>
        <div className="flex items-center justify-center gap-2 flex-wrap font-semibold text-indigo-500">
          <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
          <span>•</span>
          <Link to="/terms" className="hover:underline">Terms &amp; Conditions</Link>
          <span>•</span>
          <Link to="/refund-policy" className="hover:underline">Refund Policy</Link>
          <span>•</span>
          <Link to="/contact-us" className="hover:underline">Contact Us</Link>
        </div>
      </div>
    </AuthCard>
  );
};
